import { useState, type FormEvent } from "react";
import type { Account } from "../save/store";
import "./Family.css";

interface Props {
  onSignedIn: (account: Account) => void;
  onPrivacy: () => void;
  onBack: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function friendly(e: unknown): string {
  const kind = (e as { kind?: string }).kind;
  const msg = (e as { message?: string }).message ?? "";
  if (/rate|too many|security purposes/i.test(msg))
    return "Too many codes asked for. Wait a few minutes and try again.";
  if (kind === "network") return "Couldn't reach the server. Check the internet and try again.";
  if (/expired|invalid|token/i.test(msg))
    return "That code didn't work. Check it, or ask for a new one.";
  return "Something went wrong. Try again.";
}

/** Grown-up sign-in: email, then the 6-digit code from the email. */
export function SignIn({ onSignedIn, onPrivacy, onBack }: Props) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!EMAIL_RE.test(addr)) {
      setMsg("That doesn't look like an email address.");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const { sendCode } = await import("../cloud/api");
      await sendCode(addr);
      setEmail(addr);
      setStep("code");
      setMsg(`We've emailed a code to ${addr}.`);
    } catch (err) {
      setMsg(friendly(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) {
      setMsg("The code is 6 digits.");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const { verifyCode } = await import("../cloud/api");
      onSignedIn(await verifyCode(email, digits));
    } catch (err) {
      setMsg(friendly(err));
      setBusy(false);
    }
  };

  return (
    <section className="screen family">
      <h2 className="display family-title">Family account</h2>
      <div className="panel">
        <p>
          Sign in to keep each child&apos;s progress safe and share it between devices. We only ask
          for your email. No passwords.
        </p>
        {step === "email" ? (
          <form onSubmit={(e) => void send(e)}>
            <label htmlFor="email">Your email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" className="big display" disabled={busy}>
              {busy ? "Sending…" : "Email me a code"}
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => void verify(e)}>
            <label htmlFor="code">6-digit code</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button type="submit" className="big display" disabled={busy}>
              {busy ? "Checking…" : "Sign in"}
            </button>
            <div className="row">
              <button type="button" className="ghost" onClick={() => void send()} disabled={busy}>
                Send a new code
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setMsg("");
                }}
              >
                Use a different email
              </button>
            </div>
          </form>
        )}
        <p className="hint notice" aria-live="polite">
          {msg}
        </p>
      </div>
      <div className="row">
        <button type="button" className="ghost" onClick={onBack}>
          Back
        </button>
        <button type="button" className="ghost" onClick={onPrivacy}>
          Privacy
        </button>
      </div>
    </section>
  );
}
